import React, { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";
import Rating from "@mui/material/Rating";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import Slider from "react-slick";
import { ShoppingCart } from "lucide-react";
import Quantitybox from "../../components/quntitybox";
import Products from "../../components/Product";
import { fetchDataFromApi, putData, deleteData } from "../../utils/api"
import ReviewForm from "./ReviewForm";
import { MyContext } from "../../App";
import "./style.css";
import { useContext } from "react";

const Index = () => {
  const context = useContext(MyContext);
  const { id } = useParams();
  const [currentProduct, setCurrentProduct] = useState(null);
  const [zoomImage, setZoomImage] = useState("");
  const [activeSize, setActiveSize] = useState(null);
  const [activetab, setactivetabs] = useState(0);
  const [ProductQuantity, setProductQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [TabError, setTabError] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [user, setUser] = useState(null); // Add user state
  let [cartFields, setCartFields] = useState({});

  // Edit/Delete review states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editReviewText, setEditReviewText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Use the getCurrentUser function from context
  const getCurrentUser = context.getCurrentUser;

  const fetchReviews = async () => {
    if (!id) return;

    setReviewsLoading(true);
    try {
      const reviewsData = await fetchDataFromApi(
        `/api/productReviews?productId=${id}`
      );
      setReviews(reviewsData || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Handle edit review
  const handleEditClick = (review) => {
    setEditingReview(review);
    setEditRating(review.customerRating);
    setEditReviewText(review.review);
    setEditDialogOpen(true);
  };

  // Handle delete review
  const handleDeleteClick = (reviewId) => {
    setDeletingReviewId(reviewId);
    setDeleteDialogOpen(true);
  };

  // Submit edited review
  const handleEditSubmit = async () => {
    if (editRating === 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select a star rating!",
      });
      return;
    }

    if (editReviewText.trim().length < 10) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Review must be at least 10 characters long!",
      });
      return;
    }

    setIsProcessing(true);
    const currentUser = getCurrentUser(); // Use the function instead

    try {
      const response = await putData(`/api/productReviews/edit/${editingReview._id}`, {
        customerId: currentUser.userId,
        review: editReviewText.trim(),
        customerRating: editRating,
      });

      if (response && !response.error) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Review updated successfully!",
        });
        setEditDialogOpen(false);
        fetchReviews(); // Refresh reviews
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: response?.message || "Failed to update review",
        });
      }
    } catch (error) {
      console.error("Error updating review:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to update review. Please try again.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Confirm delete review
  const handleDeleteConfirm = async () => {
    setIsProcessing(true);
    const currentUser = getCurrentUser(); // Use the function instead

    try {
      // Use your deleteData utility with query parameter
      const result = await deleteData(`/api/productReviews/delete/${deletingReviewId}?customerId=${currentUser.userId}`);

      if (result && result.success) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Review deleted successfully!",
        });
        setDeleteDialogOpen(false);
        fetchReviews();
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: result?.message || "Failed to delete review",
        });
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to delete review. Please try again.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const canModifyReview = (review) => {
    const currentUser = getCurrentUser(); // Use the function instead
    return currentUser && currentUser.userId === review.customerId;
  };

  useEffect(() => {
    setCurrentProduct(null);
    setZoomImage("");
    setActiveSize(null);
    setProductQuantity(1);
    setTabError(false);
    setReviews([]);

    fetchDataFromApi(`/api/products/${id}`).then((res) => {
      console.log("Product data received:", res);
      setCurrentProduct(res);
      if (res?.images?.length > 0) setZoomImage(res.images[0]);

      if (res?.category?._id) {
        fetchDataFromApi(
          `/api/products?catId=${res.category._id}&limit=8`
        ).then((relatedRes) => {
          setRelatedProducts(relatedRes?.products || []);
        });
      }
    });

    fetchReviews();
  }, [id]);

  const handleReviewSubmitted = () => {
    fetchReviews();
  };

  if (!currentProduct) {
    return (
      <div className="container-fluid detailscontainer mt-5">
        <div className="row align-items-start">
          {/* Image Skeleton */}
          <div className="col-md-4">
            <div className="skeleton details-skeleton-img" />
            <div className="d-flex gap-2 mt-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton" style={{ width: '60px', height: '60px', borderRadius: '8px' }} />
              ))}
            </div>
          </div>

          {/* Info Skeleton */}
          <div className="col-md-7 productInfo">
            <div className="skeleton details-skeleton-title mb-3" />
            <div className="skeleton details-skeleton-info mb-4" />
            <hr />
            <div className="skeleton details-skeleton-price mb-4" />
            <div className="skeleton details-skeleton-stock mb-4" />
            <div className="skeleton details-skeleton-desc mb-4" />
            <div className="d-flex gap-3 mb-4">
              <div className="skeleton details-skeleton-btn" />
              <div className="skeleton details-skeleton-btn" style={{ width: '150px' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const relatedSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const handleThumbnailClick = (img, index) => {
    setZoomImage(img);
  };

  const hasWeightsOrSizes = () => {
    return (
      (Array.isArray(currentProduct.productWeight) &&
        currentProduct.productWeight.length > 0) ||
      (Array.isArray(currentProduct.sizes) && currentProduct.sizes.length > 0)
    );
  };

  const isInStock = () => {
    return currentProduct.countInStock > 0;
  };

  const styles = {
    cartBtn: {
      width: "140px",
      height: "45px",
      fontSize: "14px",
      padding: "0 16px",
      borderRadius: "25px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#28a745",
      color: "white",
      border: "none",
      fontWeight: "600",
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#218838",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(40, 167, 69, 0.3)",
      },
    },
    fontSvg: {
      fontSize: "20px",
      marginRight: "8px",
    },
    wishlistBtn: {
      width: "45px",
      height: "45px",
      borderRadius: "50%",
      backgroundColor: "#f8f9fa",
      border: "2px solid #e9ecef",
      color: "#6c757d",
      minWidth: "45px",
      marginLeft: "10px",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#ff6b6b",
        color: "white",
        borderColor: "#ff6b6b",
      },
    },
    compareBtn: {
      width: "45px",
      height: "45px",
      borderRadius: "50%",
      backgroundColor: "#f8f9fa",
      border: "2px solid #e9ecef",
      color: "#6c757d",
      minWidth: "45px",
      marginLeft: "8px",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#17a2b8",
        color: "white",
        borderColor: "#17a2b8",
      },
    },
    sizeTag: {
      padding: "8px 16px",
      border: "2px solid #e9ecef",
      borderRadius: "20px",
      backgroundColor: "white",
      color: "#495057",
      cursor: "pointer",
      textDecoration: "none",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.3s ease",
      display: "inline-block",
      margin: "4px",
    },
    activeSizeTag: {
      backgroundColor: "#28a745",
      color: "white",
      borderColor: "#28a745",
    },
    errorSizeTag: {
      backgroundColor: "#fff",
      color: "#dc3545",
      borderColor: "#dc3545",
      animation: "shake 0.5s ease-in-out",
    },
    tabBtn: {
      backgroundColor: "transparent",
      border: "none",
      padding: "12px 24px",
      color: "#6c757d",
      fontSize: "16px",
      fontWeight: "500",
      borderBottom: "3px solid transparent",
      transition: "all 0.3s ease",
      textTransform: "none",
    },
    activeTabBtn: {
      color: "#28a745",
      borderBottomColor: "#28a745",
    },
    errorTabBtn: {
      color: "#dc3545",
      borderBottomColor: "#dc3545",
      animation: "pulse 1s infinite",
    },
    priceSection: {
      padding: "20px 0",
      borderTop: "1px solid #e9ecef",
      borderBottom: "1px solid #e9ecef",
      margin: "20px 0",
    },
    currentPrice: {
      fontSize: "32px",
      fontWeight: "700",
      color: "#28a745",
    },
    oldPrice: {
      fontSize: "18px",
      color: "#6c757d",
      textDecoration: "line-through",
    },
    discount: {
      backgroundColor: "#ff6b6b",
      color: "white",
      padding: "4px 12px",
      borderRadius: "15px",
      fontSize: "14px",
      fontWeight: "600",
    },
    stockStatus: {
      fontSize: "16px",
      fontWeight: "600",
      padding: "8px 16px",
      borderRadius: "20px",
      display: "inline-block",
      marginTop: "10px",
    },
    inStock: {
      backgroundColor: "#d4edda",
      color: "#155724",
      border: "1px solid #c3e6cb",
    },
    outOfStock: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      border: "1px solid #f5c6cb",
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "12px 16px",
      borderRadius: "8px",
      border: "1px solid #f5c6cb",
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "15px",
      display: "flex",
      alignItems: "center",
    },
  };

  const quantity = (val) => {
    console.log("Quantity updated to:", val);
    setProductQuantity(val);
  };

  const selectedItem = () => { };

  const addToCart = async () => {
    console.log("Add to cart clicked");
    console.log("Current product:", currentProduct);
    console.log("Product quantity:", ProductQuantity);

    if (!isInStock()) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "This product is currently out of stock!",
      });
      return;
    }
    if (ProductQuantity > currentProduct.countInStock) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: `Only ${currentProduct.countInStock} items available in stock!`,
      });
      return;
    }

    if (hasWeightsOrSizes() && activeSize === null) {
      setTabError(true);
      setTimeout(() => {
        setTabError(false);
      }, 3000);
      return;
    }

    setTabError(false);

    // Get fresh user data each time
    const currentUser = getCurrentUser();
    console.log("Current user data:", currentUser);

    if (!currentUser || !currentUser.userId) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please login first to add items to cart!",
      });
      return;
    }

    if (!ProductQuantity || ProductQuantity <= 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select a valid quantity!",
      });
      return;
    }

    const productId = currentProduct._id || currentProduct.id;

    if (!productId) {
      console.error("No product ID found:", currentProduct);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Product ID not found. Please refresh and try again.",
      });
      return;
    }

    let selectedSize = null;
    if (hasWeightsOrSizes() && activeSize !== null) {
      if (
        Array.isArray(currentProduct.productWeight) &&
        currentProduct.productWeight.length > 0
      ) {
        selectedSize = currentProduct.productWeight[activeSize];
      } else if (
        Array.isArray(currentProduct.sizes) &&
        currentProduct.sizes.length > 0
      ) {
        selectedSize = currentProduct.sizes[activeSize];
      }
    }

    const cartData = {
      productTitle: currentProduct.name || currentProduct.title,
      image:
        currentProduct.images?.[0] ||
        currentProduct.image ||
        currentProduct.catImg,
      rating: currentProduct.rating || 0,
      price: parseFloat(currentProduct.price) || 0,
      quantity: parseInt(ProductQuantity),
      subTotal: parseFloat(currentProduct.price) * parseInt(ProductQuantity),
      productId: productId,
      countInStock: currentProduct.countInStock || 999,
      userId: currentUser.userId,
      selectedSize: selectedSize,
    };

    console.log("Cart data being sent:", cartData);

    try {
      context.setIsLoading(true);

      // Add to cart
      await context.addToCart(cartData);

      // ADD THIS STOCK UPDATE CODE
      const newStockCount = currentProduct.countInStock - ProductQuantity;

      try {
        await putData(`/api/products/${productId}`, {
          countInStock: newStockCount
        });

        // Update local state to reflect new stock count
        setCurrentProduct(prev => ({
          ...prev,
          countInStock: newStockCount
        }));

        console.log(`Stock updated: ${currentProduct.countInStock} -> ${newStockCount}`);
      } catch (stockError) {
        console.error("Error updating stock:", stockError);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Item added to cart, but stock count update failed. Please refresh the page.",
        });
      }

      if (context.getCartData) {
        await context.getCartData();
      }

    } catch (error) {

      console.error("Error in addToCart:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to add item to cart. Please try again.",
      });
    } finally {
      context.setIsLoading(false);
    }
  };

  return (
    <section className="detailpage mb-5">
      <style jsx>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }

        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Your Review</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              Rating *
            </label>
            <Rating
              value={editRating}
              onChange={(event, newValue) => setEditRating(newValue)}
              size="large"
              precision={1}
            />
          </div>
          <TextField
            label="Your Review *"
            multiline
            rows={4}
            value={editReviewText}
            onChange={(e) => setEditReviewText(e.target.value)}
            placeholder="Update your review..."
            fullWidth
            variant="outlined"
            margin="normal"
            helperText={`${editReviewText.length}/500 characters (minimum 10 characters required)`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={isProcessing}
            sx={{
              backgroundColor: "#28a745",
              "&:hover": { backgroundColor: "#218838" }
            }}
          >
            {isProcessing ? "Updating..." : "Update Review"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Review</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this review? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isProcessing}
          >
            {isProcessing ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="container-fluid detailscontainer">
        <div className="row align-items-start">
          <div className="col-md-4">
            <div className="productzoom">
              <InnerImageZoom
                zoomType="hover"
                zoomScale={1}
                src={zoomImage}
                alt={currentProduct.name}
              />
            </div>
            {/* Thumbnails Row */}
            {currentProduct.images?.length > 1 && (
              <div className="image-thumbnails-row mt-3 d-flex flex-row gap-2">
                {currentProduct.images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`thumb-${index}`}
                    className={`thumb-img ${zoomImage === img ? "active-thumb" : ""
                      }`}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      border:
                        zoomImage === img
                          ? "3px solid #28a745"
                          : "2px solid #e9ecef",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => handleThumbnailClick(img, index)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="col-md-7 productInfo">
            <h3
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#2c3e50",
                marginBottom: "16px",
              }}
            >
              {currentProduct.name}
            </h3>
            <div className="d-flex align-items-center mb-3 mt-3">
              <Rating
                name="half-rating-read"
                value={
                  reviews.length > 0
                    ? reviews.reduce(
                      (sum, review) => sum + (review.customerRating || 0),
                      0
                    ) / reviews.length
                    : parseFloat(currentProduct.rating) || 0
                }
                precision={0.5}
                readOnly
                size="large"
              />
              <span className="text-muted ms-2" style={{ fontSize: "16px" }}>
                ({reviews.length} reviews)
              </span>
            </div>
            <div
              className="priceSec d-flex align-items-center"
              style={styles.priceSection}
            >
              <span style={styles.currentPrice}>₹{currentProduct.price}</span>
              {currentProduct.discount && (
                <div className="ms-3 d-flex flex-column align-items-start">
                  <span style={styles.discount}>
                    {currentProduct.discount}% OFF
                  </span>
                  <span style={styles.oldPrice}>
                    ₹{currentProduct.oldPrice}
                  </span>
                </div>
              )}
            </div>

            {/* Stock Status */}
            <div className="stock-status mb-3">
              <span
                style={{
                  ...styles.stockStatus,
                  ...(isInStock() ? styles.inStock : styles.outOfStock),
                }}
              >
                {/* CHANGE THIS LINE */}
                {isInStock() ? `In Stock (${currentProduct.countInStock} available)` : "Out of Stock"}
              </span>
            </div>

            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#6c757d",
                margin: "20px 0",
              }}
            >
              {currentProduct.description}
            </p>

            {/* Error Message */}
            {TabError && (
              <div style={styles.errorMessage}>
                <span style={{ marginRight: "8px" }}>⚠️</span>
                Please select a size/weight before adding to cart.
              </div>
            )}

            {/* Sizes/Weights */}
            <div className="prodctSize d-flex align-items-center mb-4">
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginRight: "16px",
                }}
              >
                Size/Weight:
              </span>
              <div className="d-flex flex-wrap">
                {Array.isArray(currentProduct.productWeight) &&
                  currentProduct.productWeight.length > 0 ? (
                  currentProduct.productWeight.map((w, i) => (
                    <a
                      key={i}
                      style={{
                        ...styles.sizeTag,
                        ...(activeSize === i ? styles.activeSizeTag : {}),
                        ...(TabError && activeSize === null
                          ? styles.errorSizeTag
                          : {}),
                      }}
                      onClick={() => {
                        setActiveSize(i);
                        setTabError(false);
                      }}
                    >
                      {w}
                    </a>
                  ))
                ) : Array.isArray(currentProduct.sizes) &&
                  currentProduct.sizes.length > 0 ? (
                  currentProduct.sizes.map((size, i) => (
                    <a
                      key={i}
                      style={{
                        ...styles.sizeTag,
                        ...(activeSize === i ? styles.activeSizeTag : {}),
                        ...(TabError && activeSize === null
                          ? styles.errorSizeTag
                          : {}),
                      }}
                      onClick={() => {
                        setActiveSize(i);
                        setTabError(false);
                      }}
                    >
                      {size}
                    </a>
                  ))
                ) : (
                  <span
                    style={{
                      ...styles.sizeTag,
                      cursor: "not-allowed",
                      opacity: 0.6,
                    }}
                  >
                    Not available
                  </span>
                )}
              </div>
            </div>

            <div className="addcartSection mt-4 pt-4 d-flex align-items-center">
              <Quantitybox
                quantity={quantity}
                item={currentProduct}
                selectedItem={selectedItem}
                value={ProductQuantity}
              />

              <Button
                style={{
                  ...styles.cartBtn,
                  backgroundColor: !isInStock() ? "#6c757d" : "#28a745",
                  cursor: !isInStock() ? "not-allowed" : "pointer",
                }}
                className="cart-btn ms-3"
                onClick={addToCart}
                disabled={
                  context.isLoading || context.isAddingInCart || !isInStock()
                }
                onMouseEnter={(e) => {
                  if (
                    !context.isLoading &&
                    !context.isAddingInCart &&
                    isInStock()
                  ) {
                    e.target.style.backgroundColor = "#218838";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(40, 167, 69, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (
                    !context.isLoading &&
                    !context.isAddingInCart &&
                    isInStock()
                  ) {
                    e.target.style.backgroundColor = "#28a745";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }
                }}
              >
                <ShoppingCart style={styles.fontSvg} />
                {!isInStock()
                  ? "Out of Stock"
                  : context.isLoading || context.isAddingInCart
                    ? "Adding..."
                    : "Add to Cart"}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div
          className="card mt-5 p-5 detailsPageTabs"
          style={{ border: "1px solid #e9ecef", borderRadius: "15px" }}
        >
          <div className="customTabs">
            <ul
              className="list list-inline border-bottom"
              style={{ paddingBottom: "0" }}
            >
              {[
                "Description",
                "Additional Info",
                "Product Data",
                "Reviews",
              ].map((tab, i) => (
                <li className="list-inline-item" key={i}>
                  <Button
                    style={{
                      ...styles.tabBtn,
                      ...(activetab === i ? styles.activeTabBtn : {}),
                      ...(TabError && i === 0 ? styles.errorTabBtn : {}),
                    }}
                    onClick={() => setactivetabs(i)}
                  >
                    {tab}
                  </Button>
                </li>
              ))}
            </ul>
            <br />
            <div style={{ minHeight: "200px", padding: "20px 0" }}>
              {activetab === 0 && (
                <div
                  className="tabContent"
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.8",
                    color: "#495057",
                  }}
                >
                  {currentProduct.longDescription || currentProduct.description}
                </div>
              )}
              {activetab === 1 && (
                <div
                  className="tabContent"
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.8",
                    color: "#495057",
                  }}
                >
                  {currentProduct.additionalInfo ||
                    "Additional information not available."}
                </div>
              )}


              {activetab === 2 && (
                <div className="tabContent">
                  <table className="table table-striped">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Product Name</strong>
                        </td>
                        <td>{currentProduct.name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Brand</strong>
                        </td>
                        <td>{currentProduct.brand || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Category</strong>
                        </td>
                        <td>{currentProduct.catName || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Subcategory</strong>
                        </td>
                        <td>{currentProduct.subCat || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Price</strong>
                        </td>
                        <td>₹{currentProduct.price || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Old Price</strong>
                        </td>
                        <td>{currentProduct.oldPrice ? `₹${currentProduct.oldPrice}` : "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Discount</strong>
                        </td>
                        <td>{currentProduct.discount ? `${currentProduct.discount}%` : "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Stock Count</strong>
                        </td>
                        <td>{currentProduct.countInStock || "0"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Rating</strong>
                        </td>
                        <td>{currentProduct.rating ? `${currentProduct.rating}/5` : "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Featured Product</strong>
                        </td>
                        <td>{currentProduct.isFeatured ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Product Weight/Size</strong>
                        </td>
                        <td>
                          {Array.isArray(currentProduct.productWeight) && currentProduct.productWeight.length > 0
                            ? currentProduct.productWeight.join(", ")
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Location</strong>
                        </td>
                        <td>{currentProduct.location || "N/A"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Date Created</strong>
                        </td>
                        <td>
                          {currentProduct.dateCreated
                            ? new Date(currentProduct.dateCreated).toLocaleDateString()
                            : currentProduct.createdAt
                              ? new Date(currentProduct.createdAt).toLocaleDateString()
                              : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Last Updated</strong>
                        </td>
                        <td>
                          {currentProduct.updatedAt
                            ? new Date(currentProduct.updatedAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {activetab === 3 && (
                <div className="tabContent">
                  {reviewsLoading ? (
                    <div className="existing-reviews mb-4">
                      {[1, 2].map(i => (
                        <div key={i} className="mb-4 p-3" style={{ border: '1px solid #e9ecef', borderRadius: '8px' }}>
                          <div className="skeleton mb-2" style={{ width: '40%', height: '20px' }}></div>
                          <div className="skeleton" style={{ width: '100%', height: '40px' }}></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {reviews && reviews.length > 0 ? (
                        <div className="existing-reviews mb-4">
                          <h5 style={{ marginBottom: "20px", fontWeight: 600 }}>
                            Customer Reviews ({reviews.length})
                          </h5>
                          {reviews.map((review, index) => (
                            <div
                              key={review._id || index}
                              className="review-item mb-4 p-3"
                              style={{
                                border: "1px solid #e9ecef",
                                borderRadius: "8px",
                                backgroundColor: "#fff",
                                position: "relative",
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center">
                                  <Rating
                                    value={review.customerRating || 0}
                                    readOnly
                                    size="small"
                                  />
                                  <span
                                    className="ms-2 fw-bold"
                                    style={{ color: "#2c3e50" }}
                                  >
                                    {review.customerName}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span
                                    style={{ fontSize: "12px", color: "#6c757d", marginRight: "10px" }}
                                  >
                                    {review.dateCreated
                                      ? new Date(
                                        review.dateCreated
                                      ).toLocaleDateString()
                                      : "Recently"}
                                  </span>
                                  {canModifyReview(review) && (
                                    <div>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditClick(review)}
                                        sx={{
                                          color: "#28a745",
                                          "&:hover": {
                                            backgroundColor: "rgba(40, 167, 69, 0.1)",
                                          },
                                        }}
                                        title="Edit review"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteClick(review._id)}
                                        sx={{
                                          color: "#dc3545",
                                          "&:hover": {
                                            backgroundColor: "rgba(220, 53, 69, 0.1)",
                                          },
                                        }}
                                        title="Delete review"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p
                                style={{
                                  margin: 0,
                                  lineHeight: "1.6",
                                  color: "#495057",
                                }}
                              >
                                {review.review}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          className="no-reviews mb-4"
                          style={{ textAlign: "center", padding: "40px" }}
                        >
                          <p style={{ fontSize: "16px", color: "#6c757d" }}>
                            No reviews yet. Be the first to review this product!
                          </p>
                        </div>
                      )}

                      <ReviewForm
                        productId={id}
                        onReviewSubmitted={handleReviewSubmitted}
                        context={context}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="related-products mt-5">
            <h4
              style={{
                fontSize: "24px",
                fontWeight: "700",
                marginBottom: "30px",
                color: "#2c3e50",
              }}
            >
              Related Products
            </h4>
            <Slider {...relatedSettings}>
              {relatedProducts.map((product, index) => (
                <div key={product._id || index} className="px-2">
                  <Products item={product} />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </section>
  );
};

export default Index;